import React, { useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ============================================
// DESIGN TOKENS
// ============================================
const tokens = {
    colors: {
        surface: 'rgba(18, 18, 20, 0.95)',
        surfaceHover: 'rgba(22, 22, 26, 0.98)',
        border: 'rgba(255, 255, 255, 0.1)',
        borderHover: 'rgba(255, 255, 255, 0.2)',
        text: '#ffffff',
        textMuted: 'rgba(255, 255, 255, 0.6)',
        accent: '#c80707',
        accentHover: '#e00808',
        accentGlow: 'rgba(200, 7, 7, 0.5)',
        disabled: 'rgba(255, 255, 255, 0.3)',
        disabledBg: 'rgba(80, 80, 80, 0.5)',
    },
    blur: '24px',
    radius: '16px',
    radiusSmall: '10px',
    shadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)',
    shadowHover: '0 20px 64px rgba(0, 0, 0, 0.6), 0 8px 24px rgba(0, 0, 0, 0.4)',
};

// ============================================
// ANIMATIONS
// ============================================
const fadeIn = keyframes`
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================
const MapWrapper = styled.div<{ $expanded: boolean }>`
    position: fixed;
    bottom: 60px;
    right: 12px;
    z-index: 1000;
    animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);

    /* Smooth size transitions - specific properties only */
    width: ${props => props.$expanded ? '650px' : '320px'};
    height: ${props => props.$expanded ? '500px' : '220px'};
    transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                height 0.4s cubic-bezier(0.16, 1, 0.3, 1);

    @media (max-width: 960px) {
        display: none;
    }

    /* Malé obrazovky / nízká výška */
    @media (max-height: 700px) {
        bottom: 50px;
        width: ${props => props.$expanded ? '550px' : '280px'};
        height: ${props => props.$expanded ? '420px' : '180px'};
    }

    /* Střední monitory (1080p - 1440p) */
    @media (min-width: 1400px) and (min-height: 800px) {
        width: ${props => props.$expanded ? 'min(75vw, 1000px)' : '350px'};
        height: ${props => props.$expanded ? 'min(75vh, 700px)' : '240px'};
    }

    /* Velké monitory (1440p+) */
    @media (min-width: 2000px) and (min-height: 1000px) {
        width: ${props => props.$expanded ? 'min(70vw, 1400px)' : '400px'};
        height: ${props => props.$expanded ? 'min(75vh, 900px)' : '280px'};
        bottom: 70px;
        right: 16px;
    }

    /* 4K monitory (2160p+) */
    @media (min-width: 2560px) and (min-height: 1400px) {
        width: ${props => props.$expanded ? 'min(65vw, 1800px)' : '480px'};
        height: ${props => props.$expanded ? 'min(80vh, 1400px)' : '340px'};
        bottom: 80px;
        right: 20px;
    }

    /* Ultrawide monitory */
    @media (min-width: 3000px) {
        width: ${props => props.$expanded ? 'min(55vw, 2000px)' : '520px'};
        height: ${props => props.$expanded ? 'min(80vh, 1500px)' : '380px'};
    }
`;

const MapContainer = styled.div<{ $expanded: boolean }>`
    width: 100%;
    height: 100%;
    background: ${tokens.colors.surface};
    backdrop-filter: blur(${tokens.blur});
    -webkit-backdrop-filter: blur(${tokens.blur});
    border: 1px solid ${props => props.$expanded ? tokens.colors.borderHover : tokens.colors.border};
    border-radius: ${tokens.radius};
    box-shadow: ${props => props.$expanded ? tokens.shadowHover : tokens.shadow};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    /* Specific transitions - avoid transitioning layout properties that affect Leaflet */
    transition: border-color 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1);

    /* Subtle glow on hover */
    &::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: ${tokens.radius};
        padding: 1px;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 50%,
            rgba(255, 255, 255, 0.05) 100%
        );
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
        opacity: ${props => props.$expanded ? 1 : 0};
        transition: opacity 0.4s ease;
    }
`;

const MapHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid ${tokens.colors.border};
    background: rgba(0, 0, 0, 0.2);
`;

const MapTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.colors.textMuted};
`;

const MapIcon = styled.div`
    width: 18px;
    height: 18px;
    color: ${tokens.colors.accent};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ExpandHint = styled.div<{ $visible: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Work Sans', sans-serif;
    font-size: 11px;
    color: ${tokens.colors.textMuted};
    opacity: ${props => props.$visible ? 0.7 : 0};
    transition: opacity 0.3s ease;

    svg {
        width: 14px;
        height: 14px;
    }
`;

const MapContent = styled.div<{ $isTransitioning?: boolean }>`
    flex: 1;
    position: relative;
    overflow: hidden;
    background: #1a1a1c;

    /* Map placeholder styling */
    & > div,
    & > .smap,
    & > #smap {
        width: 100% !important;
        height: 100% !important;
    }

    /* Prevent Leaflet elements from inheriting transitions - critical for smooth resize */
    .leaflet-container,
    .leaflet-container * {
        transition: none !important;
    }

    /* Keep the map pane stable during resize */
    .leaflet-map-pane {
        will-change: transform;
    }

    /* Subtle fade effect for tile loading during resize */
    .leaflet-tile-container {
        transition: opacity 0.15s ease-out;
    }

    /* Hide loading tiles background during transition */
    .leaflet-tile {
        background: transparent !important;
    }
`;

const MapFooter = styled.div`
    padding: 12px 16px;
    background: rgba(0, 0, 0, 0.3);
    border-top: 1px solid ${tokens.colors.border};
`;

const GuessButton = styled.button<{ $disabled: boolean; $isNextRound?: boolean }>`
    width: 100%;
    padding: 14px 24px;
    border: none;
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.02em;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    overflow: hidden;

    ${props => props.$disabled ? css`
        background: ${tokens.colors.disabledBg};
        color: ${tokens.colors.disabled};
    ` : css`
        background: linear-gradient(135deg, ${tokens.colors.accent} 0%, #a00606 100%);
        color: ${tokens.colors.text};
        box-shadow: 0 4px 16px ${tokens.colors.accentGlow};

        &:hover {
            background: linear-gradient(135deg, ${tokens.colors.accentHover} 0%, #c00707 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 24px ${tokens.colors.accentGlow};
        }

        &:active {
            transform: translateY(0);
        }
    `}

    ${props => props.$isNextRound && !props.$disabled && css`
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        box-shadow: 0 4px 16px rgba(34, 197, 94, 0.4);

        &:hover {
            background: linear-gradient(135deg, #2dd970 0%, #1db954 100%);
            box-shadow: 0 8px 24px rgba(34, 197, 94, 0.5);
        }
    `}

    /* Shimmer effect on hover */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
        );
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover::after {
        opacity: ${props => props.$disabled ? 0 : 1};
        animation: ${shimmer} 1.5s ease-in-out infinite;
    }
`;

const ButtonContent = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    position: relative;
    z-index: 1;
`;

const ButtonIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
`;

const PlaceholderText = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: ${tokens.colors.textMuted};
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    text-align: center;
    padding: 20px;

    svg {
        width: 32px;
        height: 32px;
        opacity: 0.5;
    }
`;

const GuessedStatus = styled.div`
    width: 100%;
    padding: 14px 24px;
    border-radius: ${tokens.radiusSmall};
    font-family: 'Work Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%);
    border: 1px solid rgba(34, 197, 94, 0.3);
    color: #22c55e;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const CheckIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: #22c55e;
    border-radius: 50%;
    color: white;
    font-size: 12px;
`;

// ============================================
// COMPONENT
// ============================================
interface FloatingMiniMapProps {
    children: React.ReactNode;
    onGuess: () => void;
    onNextRound?: () => void;
    disabled?: boolean;
    showNextRound?: boolean;
    buttonText?: string;
    isLoading?: boolean;
    hasGuessed?: boolean;
    isBattle?: boolean;
}

export const FloatingMiniMap: React.FC<FloatingMiniMapProps> = ({
    children,
    onGuess,
    onNextRound,
    disabled = false,
    showNextRound = false,
    buttonText,
    isLoading = false,
    hasGuessed = false,
    isBattle = false,
}) => {
    const [expanded, setExpanded] = useState(false);

    const handleMouseEnter = useCallback(() => {
        setExpanded(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setExpanded(false);
    }, []);

    const handleClick = useCallback(() => {
        if (showNextRound && onNextRound) {
            onNextRound();
        } else {
            onGuess();
        }
    }, [showNextRound, onGuess, onNextRound]);

    const getButtonText = () => {
        if (buttonText) return buttonText;
        if (showNextRound) return 'Další kolo';
        if (disabled) return 'Klikni na mapu';
        return 'Hádat!';
    };

    return (
        <MapWrapper
            $expanded={expanded}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <MapContainer $expanded={expanded}>
                <MapHeader>
                    <MapTitle>
                        <MapIcon>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                        </MapIcon>
                        Mapa
                    </MapTitle>
                    <ExpandHint $visible={!expanded}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" />
                            <polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" />
                            <line x1="3" y1="21" x2="10" y2="14" />
                        </svg>
                        Najeď pro zvětšení
                    </ExpandHint>
                </MapHeader>

                <MapContent>
                    {children}
                </MapContent>

                <MapFooter>
                    {/* Show "Tip umístěn" status when player has guessed in battle mode */}
                    {isBattle && hasGuessed && !showNextRound ? (
                        <GuessedStatus>
                            <CheckIcon>✓</CheckIcon>
                            Tip umístěn – čekám na ostatní
                        </GuessedStatus>
                    ) : (
                    <GuessButton
                        $disabled={disabled && !showNextRound}
                        $isNextRound={showNextRound}
                        disabled={disabled && !showNextRound}
                        onClick={handleClick}
                    >
                        <ButtonContent>
                            <ButtonIcon>
                                {showNextRound ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 8v4l3 3" />
                                    </svg>
                                )}
                            </ButtonIcon>
                            {getButtonText()}
                        </ButtonContent>
                    </GuessButton>
                    )}
                </MapFooter>
            </MapContainer>
        </MapWrapper>
    );
};

export default FloatingMiniMap;
